import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  Add as AddIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  ViewColumn as ColumnIcon,
  InfoOutlined,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import LevelOpexService from "../service/levelOpexService";

const Level3Table = () => {
  const tableRef = useRef(null);
  const [table, setTable] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ids: [] });
  const [addDialog, setAddDialog] = useState({ open: false });
  const [newRecord, setNewRecord] = useState({
    level3_code: "",
    level3_desc: "",
    level2_code: "",
    // Campi gerarchici inizializzati vuoti
    level2_desc: "",
    level1_code: "",
    level1_desc: "",
    // Campi aggiuntivi inizializzati vuoti
    sap_conto: "",
    sap_cdc: "",
    sap_ou: "",
    sap_ou_desc: "",
    local_corporate: "",
    responsible: "",
  });
  const [level2Options, setLevel2Options] = useState([]);
  const [filtersActive, setFiltersActive] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    hierarchical: true,
    details: false,
  });

  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  const handleAddDialogOpen = () => {
    setAddDialog({ open: true });
    fetchLevel2Options();
  };

  const handleAddDialogClose = () => {
    setAddDialog({ open: false });
    setNewRecord({
      level3_code: "",
      level3_desc: "",
      level2_code: "",
      level2_desc: "",
      level1_code: "",
      level1_desc: "",
      sap_conto: "",
      sap_cdc: "",
      sap_ou: "",
      sap_ou_desc: "",
      local_corporate: "",
      responsible: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecord((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchData = async () => {
    try {
      const data = await LevelOpexService.getLevels3();
      if (table) {
        table.setData(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("Error loading data", "error");
    }
  };

  const fetchLevel2Options = async () => {
    try {
      const data = await LevelOpexService.getLevels2();
      setLevel2Options(data);
    } catch (error) {
      console.error("Error fetching Level 2 data:", error);
      showNotification("Error loading Level 2 options", "error");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Level3 Data");
      XLSX.writeFile(wb, "Level3_data.xlsx");
    }
  };

  const handleAddNew = async () => {
    if (
      !newRecord.level3_code ||
      !newRecord.level3_desc ||
      !newRecord.level2_code
    ) {
      showNotification("Compilare tutti i campi obbligatori.", "error");
      return;
    }
    try {
      await LevelOpexService.insertLevel3(newRecord);
      await fetchData();
      showNotification("New record added successfully", "success");
      handleAddDialogClose();
    } catch (error) {
      console.error("Error adding new record:", error);
      showNotification("Error adding new record", "error");
    }
  };

  const handleDeleteSelected = () => {
    const selectedRows = table.getSelectedRows();
    if (selectedRows.length === 0) {
      showNotification("Selezionare quali record eliminare.", "warning");
      return;
    }
    const selectedIds = selectedRows.map((row) => row.getData().id);
    setDeleteDialog({ open: true, ids: selectedIds });
  };

  const handleDelete = async (ids) => {
    try {
      for (const id of ids) {
        await LevelOpexService.deleteLevel3(id);
      }
      await fetchData();
      showNotification(
        `Successfully deleted ${ids.length} record(s)`,
        "success"
      );
    } catch (error) {
      console.error("Error deleting records:", error);
      showNotification("Error deleting records", "error");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, ids: [] });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.ids.length > 0) {
      await handleDelete(deleteDialog.ids);
      setDeleteDialog({ open: false, ids: [] });
    }
  };

  useEffect(() => {
    if (table) {
      const column = table.getColumn("level2_code");
      LevelOpexService.getLevels2().then((data) => {
        const values = data.map((item) => ({
          value: item.level2_code,
          label: `${item.level2_code} - ${item.level2_desc}`,
        }));
        column.updateDefinition({
          editorParams: {
            values,
            displayField: "label",
            valueField: "value",
          },
          headerFilterParams: {
            values,
            displayField: "label",
            valueField: "value",
          },
        });
      });
    }
  }, [table]);

  useEffect(() => {
    if (table) {
      const tableElement = table.element;
      tableElement.classList.add("hide-filters");
    }
  }, [table]);

  useEffect(() => {
    let tableInstance = null;

    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      }

      const columns = [
        {
          formatter: "rowSelection",
          titleFormatter: "rowSelection",
          titleFormatterParams: { rowRange: "active" },
          hozAlign: "center",
          headerSort: false,
          width: 30,
          frozen: true,
          cellClick: function (_, cell) {
            const row = cell.getRow();

            if (row.isSelected()) {
              row.deselect();
            } else {
              row.select();
            }
          },
        },
        {
          title: "Level3 Code",
          field: "level3_code",
          widthGrow: 1,
          editor: "input",
          validator: ["required"],
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Description",
          field: "level3_desc",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Level2 Code",
          field: "level2_code",
          widthGrow: 1,
          editor: "list",
          validator: "required",
          editable: true,
          editorParams: {},
          headerFilter: "list",
          headerFilterParams: {},
          cssClass: "editable-column",
        },
        {
          title: "Level2 Description",
          field: "level2_desc",
          widthGrow: 2,
          headerFilter: "input",
          editable: false,
          frozen: true,
        },
        {
          title: "Level1 Code",
          field: "level1_code",
          widthGrow: 1,
          headerFilter: "input",
          editable: false,
          frozen: true,
        },
        {
          title: "Level1 Description",
          field: "level1_desc",
          widthGrow: 2,
          headerFilter: "input",
          editable: false,
          frozen: true,
        },
        {
          title: "SAP Conto",
          field: "sap_Conto",
          widthGrow: 1,
          editor: "input",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "SAP CDC",
          field: "sap_Cdc",
          widthGrow: 1,
          editor: "input",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "SAP OU",
          field: "sap_OU",
          widthGrow: 1,
          editor: "input",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "SAP OU Description",
          field: "sap_OU_desc",
          widthGrow: 2,
          editor: "input",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Local/Corporate",
          field: "local_Corporate",
          widthGrow: 1,
          editor: "select",
          editable: true,
          headerFilter: "list",
          editorParams: {
            values: ["Local", "Corporate", ""],
          },
          headerFilterParams: {
            values: ["", "Local", "Corporate"],
          },
          cssClass: "editable-column",
        },
        {
          title: "Responsible",
          field: "responsible",
          widthGrow: 1.5,
          editor: "input",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
      ];

      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitDataFill",
        pagination: "local",
        paginationSize: 100,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        initialSort: [{ column: "level3_code", dir: "asc" }],
        rowHeight: 40,
        selectable: true,
        selectableRangeMode: "click",
        selectablePersistence: true,
        responsiveLayout: false,
        autoResize: false,
        selectableRange: true,
        selectableRangeColumns: false,
        selectableRangeRows: true,
        selectableRangeClearCells: true,
        navigationMode: "cell",
        clipboard: true,
        clipboardCopyRowRange: "range",
        clipboardPasteParser: "range",
        clipboardPasteAction: "range",
        editTriggerEvent: "dblclick",
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filter...",
        clipboardCopyConfig: {
          rowHeaders: false,
          columnHeaders: false,
        },
        clipboardCopyStyled: false,
      });

      newTable.on("cellEdited", async (cell) => {
        try {
          const row = cell.getRow();
          const rowData = row.getData();

          if (cell.getField() === "level2_code") {
            const level2Data = await LevelOpexService.getLevels2();
            const selectedLevel2 = level2Data.find(
              (item) => item.level2_code === cell.getValue()
            );

            if (selectedLevel2) {
              // Aggiorna automaticamente i campi dipendenti
              row.update({
                level2_desc: selectedLevel2.level2_desc,
                level1_code: selectedLevel2.level1_code,
                level1_desc: selectedLevel2.level1_desc,
              });

              // Aggiorna anche rowData per la chiamata API
              rowData.level2_desc = selectedLevel2.level2_desc;
              rowData.level1_code = selectedLevel2.level1_code;
              rowData.level1_desc = selectedLevel2.level1_desc;
            }

            const values = level2Data.map((item) => ({
              value: item.level2_code,
              label: `${item.level2_code} - ${item.level2_desc}`,
            }));

            cell
              .getTable()
              .getColumn("level2_code")
              .updateDefinition({
                editorParams: {
                  values,
                  displayField: "label",
                  valueField: "value",
                },
                headerFilterParams: {
                  values,
                  displayField: "label",
                  valueField: "value",
                },
              });
          }

          const updatedData = await LevelOpexService.updateLevel3(rowData);
          cell.getTable().updateData([updatedData]);
          showNotification("Changes saved successfully", "success");
        } catch (error) {
          console.error("Error saving data:", error);
          cell.restoreOldValue();
          showNotification("Error saving changes", "error");
        }
      });

      setTable(newTable);

      return () => {
        if (table) {
          table.destroy();
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, []);

  useEffect(() => {
    if (table) {
      fetchData();
    }
  }, [table]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Q8Breadcrumbs
        links={[{ label: "Level 3", href: "/level3" }]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Level3 OPEX
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="primary"
            label="Aggiungi nuovo"
            icon={<AddIcon />}
            onClick={handleAddDialogOpen}
            size="small"
          />
          <Q8Button
            variant="secondary"
            label="Export Excel"
            icon={<ExportIcon />}
            onClick={handleDownloadExcel}
            size="small"
          />
          <Q8Button
            variant="tertiary"
            label={filtersActive ? "Hide Filters" : "Show Filters"}
            icon={<FilterIcon />}
            onClick={() => {
              if (table) {
                const newState = !filtersActive;
                setFiltersActive(newState);

                const tableElement = table.element;
                if (newState) {
                  tableElement.classList.remove("hide-filters");
                } else {
                  tableElement.classList.add("hide-filters");
                  table.clearHeaderFilter();
                }
              }
            }}
            size="small"
          />
          <Q8Button
            variant="error"
            label="Delete Selected"
            icon={<DeleteIcon />}
            onClick={handleDeleteSelected}
            size="small"
          />
        </Box>
      </Box>

      <Box
        sx={{
          my: 1,
          p: 2,
          bgcolor: "#FFF8E1",
          border: "1px solid #FFE082",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <InfoOutlined sx={{ color: "#FB8C00" }} />
        <Typography variant="body2">
          Per modificare un record, scegliere una colonna modificabile (colonne
          in giallo) ed effettuare un doppio click sulla cella per effettuare
          una modifica.
        </Typography>
      </Box>

      <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={tableRef} style={{ flex: 1 }}></div>
      </Card>

      <Dialog
        open={addDialog.open}
        onClose={handleAddDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Aggiungi nuovo Record Livello 3</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              py: 2,
            }}
          >
            {/* Campi obbligatori */}
            <TextField
              label="Level3 Code"
              name="level3_code"
              value={newRecord.level3_code}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.level3_code}
              helperText={!newRecord.level3_code ? "Campo obbligatorio" : ""}
            />
            <TextField
              label="Description"
              name="level3_desc"
              value={newRecord.level3_desc}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.level3_desc}
              helperText={!newRecord.level3_desc ? "Campo obbligatorio" : ""}
            />
            <TextField
              select
              label="Level2 Code"
              name="level2_code"
              value={newRecord.level2_code}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.level2_code}
              helperText={!newRecord.level2_code ? "Campo obbligatorio" : ""}
            >
              {level2Options.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.level2_code}
                  onClick={() => {
                    setNewRecord((prev) => ({
                      ...prev,
                      level2_code: option.level2_code,
                      level2_desc: option.level2_desc || "",
                      level1_code: option.level1_code || "",
                      level1_desc: option.level1_desc || "",
                    }));
                  }}
                >
                  {option.level2_code} - {option.level2_desc}
                </MenuItem>
              ))}
            </TextField>

            {/* Campi aggiunti da Level1 */}
            <TextField
              label="SAP Conto"
              name="sap_conto"
              value={newRecord.sap_conto || ""}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="SAP CDC"
              name="sap_cdc"
              value={newRecord.sap_cdc || ""}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="SAP OU"
              name="sap_ou"
              value={newRecord.sap_ou || ""}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="SAP OU Description"
              name="sap_ou_desc"
              value={newRecord.sap_ou_desc || ""}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              select
              label="Local/Corporate"
              name="local_corporate"
              value={newRecord.local_corporate || ""}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="Local">Local</MenuItem>
              <MenuItem value="Corporate">Corporate</MenuItem>
            </TextField>
            <TextField
              label="Responsible"
              name="responsible"
              value={newRecord.responsible || ""}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleAddDialogClose}
          />
          <Q8Button variant="primary" label="Aggiungi" onClick={handleAddNew} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Sei sicuro di voler eliminare {deleteDialog.ids.length}
            record(s) selezionati? Quest'azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleDeleteCancel}
          />
          <Q8Button
            variant="primary"
            label="Delete"
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: "#cc0e00" }}
          />
        </DialogActions>
      </Dialog>

      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default Level3Table;
